// src/pages/projects/components/DocumentListItem.jsx - Updated with unified permissions
import React from 'react';
import {
  Box,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Chip,
  IconButton
} from '@mui/material';
import {
  Description as DocumentIcon,
  Image as ImageIcon,
  PictureAsPdf as PDFIcon,
  Code as CodeIcon,
  TableChart as SpreadsheetIcon,
  Slideshow as PresentationIcon,
  AttachFile as AttachmentIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';

import { DOCUMENT_CATEGORIES } from '../../../constants/documentConstants';
import { formatDate } from '../../../utils/dateUtils';
import { SecureAvatar, SecureFileDownload } from '../../../components/ui';
import useProjectPermissions from '../../../hooks/useProjectPermissions';

// Map file types to icons
const iconMap = {
  document: <DocumentIcon />,
  image: <ImageIcon />,
  pdf: <PDFIcon />,
  spreadsheet: <SpreadsheetIcon />,
  presentation: <PresentationIcon />,
  code: <CodeIcon />,
  attachment: <AttachmentIcon />
};

// Render the correct icon based on file type
const renderFileIcon = (type) => {
  return iconMap[type] || <DocumentIcon />;
};

/**
 * Displays a document item in the document list
 * 
 * @param {Object} props - Component props
 * @param {Object} props.document - Document object
 * @param {string} props.fileType - Type of file for icon selection
 * @param {boolean} props.canEdit - DEPRECATED: Using permissions hook instead
 * @param {string} props.projectId - Project ID for permission checks
 * @param {Function} props.onDelete - Function to handle document deletion
 * @param {Function} props.onEdit - Function to handle document editing
 */
const DocumentListItem = ({ 
  document, 
  fileType, 
  canEdit = false, // DEPRECATED: Using permissions hook instead
  projectId,
  onDelete,
  onEdit
}) => {
  // Use the unified permissions hook
  const { canDeleteFiles, canUploadFiles } = useProjectPermissions(projectId);
  
  // Determine if edit/delete actions should be shown
  // If projectId is available, use specific permission; otherwise fall back to canEdit
  const showEditButton = projectId ? canUploadFiles : canEdit; // Edit uses upload permission
  const showDeleteButton = projectId ? canDeleteFiles : canEdit;

  return (
    <ListItem>
      <ListItemIcon>
        {renderFileIcon(fileType)}
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="subtitle2">
            {document.name}
          </Typography>
        }
        secondary={
          <Box>
            {document.description && (
              <Typography variant="body2" color="textSecondary">
                {document.description}
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              <Chip 
                label={DOCUMENT_CATEGORIES.find(cat => cat.value === document.type)?.label || 'Other'} 
                size="small" 
              />
              
              {document.uploadedBy && (
                <Chip
                  avatar={
                    <SecureAvatar
                      src={document.uploadedBy.photo}
                      alt={document.uploadedBy.firstName}
                      sx={{ width: 24, height: 24 }}
                    >
                      {document.uploadedBy?.firstName?.[0]}
                    </SecureAvatar>
                  }
                  label={`${document.uploadedBy.firstName} ${document.uploadedBy.lastName}`}
                  size="small"
                  variant="outlined"
                />
              )}
              
              <Chip
                label={`Uploaded: ${formatDate(document.uploadedAt)}`}
                size="small"
                variant="outlined"
              />
              
              {document.isClientVisible && (
                <Chip 
                  label="Client Visible" 
                  size="small" 
                  color="secondary"
                />
              )}
            </Box>
          </Box>
        }
      />
      
      <ListItemSecondaryAction>
        <SecureFileDownload
          file={document.file}
          tooltip="Download document"
          onError={(error) => console.error('Download failed:', error)}
        />
        
        {showEditButton && (
          <IconButton 
            edge="end" 
            onClick={() => onEdit(document)}
            sx={{ ml: 1 }}
            title="Edit document"
          >
            <EditIcon />
          </IconButton>
        )}
        
        {showDeleteButton && (
          <IconButton 
            edge="end" 
            color="error"
            onClick={() => onDelete(document)}
            sx={{ ml: 1 }}
            title="Delete document"
          >
            <DeleteIcon />
          </IconButton>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export default DocumentListItem;